/**
 * Hook das tabelas Curva Curta/Longa do PnL (Task #55).
 *
 * D0 + MWM/MWH vêm vivos do /api/curva-pnl; D-1/Δ/PnL só acendem quando há
 * snapshot do dia anterior (`d1_date != null`) — até lá `d1Pendente=true` e a
 * UI mostra o selo "aguardando 2º snapshot". Cache module-level + refetch 10min.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getCurvaPnl } from "../api/client";
import type { CurvaPnlResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

const EMPTY: CurvaPnlResponse = {
  curta: { linhas: [], d1_date: null },
  longa: { linhas: [], total_mwh: 0, total_pnl: null, d1_date: null },
  resultado: {
    book: "Brasil", as_of: null,
    fv_diario: null, fv_semanal: null, fv_mensal: null, fv_anual: null,
    pv_diario: null, pv_semanal: null, pv_mensal: null, pv_anual: null,
    curva_pre_asof: null,
    duration: null, sigma_cf: null,
    d1_date: null, dsem_date: null, dmes_date: null, dano_date: null,
  },
};

interface CacheEntry {
  expiresAt: number;
  promise: Promise<CurvaPnlResponse>;
}
let cache: CacheEntry | null = null;

export function invalidateCurvaPnlCache(): void {
  cache = null;
}

export interface UseCurvaPnlState {
  data: CurvaPnlResponse;
  isLoading: boolean;
  error: Error | null;
  /** D-1 ainda não existe (snapshot único) → Δ/PnL pendentes. */
  d1Pendente: boolean;
  /** Fail-closed (incidente 02/08): invalida o cache e refaz o fetch — a tela
   * de erro oferece "tentar de novo" em vez de cair em mock. */
  retry: () => void;
}

export function useCurvaPnl(): UseCurvaPnlState {
  const retryRef = useRef<() => void>(() => {});
  const retry = useCallback(() => retryRef.current(), []);
  const [state, setState] = useState<UseCurvaPnlState>({
    data: EMPTY,
    isLoading: true,
    error: null,
    d1Pendente: true,
    retry,
  });

  useEffect(() => {
    let cancel = false;
    function load() {
      const now = Date.now();
      if (!cache || cache.expiresAt < now) {
        cache = { expiresAt: now + REFETCH_MS, promise: getCurvaPnl() };
      }
      cache.promise
        .then((data) => {
          if (cancel) return;
          setState({
            data,
            isLoading: false,
            error: null,
            d1Pendente: data.longa.d1_date === null && data.curta.d1_date === null,
            retry,
          });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache = null;
          setState({ data: EMPTY, isLoading: false, error: err, d1Pendente: true, retry });
        });
    }
    // Fail-closed (incidente 02/08): "tentar de novo" da tela de erro —
    // invalida o cache module-level e refaz o fetch neste mount.
    retryRef.current = () => {
      invalidateCurvaPnlCache();
      setState((s) => ({ ...s, isLoading: true, error: null }));
      load();
    };
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [retry]);

  return state;
}
